"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useListSearch = useListSearch;
const react_1 = require("react");
const dataEvents_1 = require("./dataEvents");
/** 查過的結果（記憶體，不 persist）：換回用過的條件時先顯示、背景更新；只留最近 30 組 */
const CACHE_LIMIT = 30;
const resultCache = new Map();
const cacheKeyOf = (topic, key, page) => `${topic}|${page}|${key}`;
/**
 * 列表的搜尋／篩選由後端查全部資料（docs/介面優化.md R29；只過濾 store 裡目前那一頁的話，第 2 頁以後搜不到）
 * - query 為 null（沒有任何搜尋條件）時不查詢，頁面照用列表 store（含快取、骨架、預載入）
 * - 換條件時保留上一次的結果，新的回來才換掉（同 React Query keepPreviousData），不閃骨架；
 *   第一次有條件、結果還沒回來前 active 是 false，頁面繼續顯示列表 store
 * - 只有打字輸入的欄位（預設 query.search，可用 textFields 指定）變了才等 300ms 再查（打字中不送）；選階段、標籤等篩選馬上查
 * - 用過的條件、頁碼先顯示記憶體裡的結果，同時向後端更新；setPage 換頁
 * - initialPage：進頁面時的條件查這一頁（網址 ?page=，從詳情頁返回回到同一頁）；之後改條件一律回第 1 頁
 * - 資料異動（notifyDataChanged(topic)）時清掉這個主題的結果、重新查目前這一頁
 * @param onError 查詢失敗時（各系統傳 toast 錯誤提示）
 */
function useListSearch(topic, query, fetchPage, onError, initialPage = 1, { textFields = ['search'] } = {}) {
    const [result, setResult] = (0, react_1.useState)(null);
    const [isFetching, setIsFetching] = (0, react_1.useState)(false);
    const key = query ? JSON.stringify(query) : null;
    const keyRef = (0, react_1.useRef)(key);
    keyRef.current = key;
    const fetchRef = (0, react_1.useRef)(fetchPage);
    fetchRef.current = fetchPage;
    const onErrorRef = (0, react_1.useRef)(onError);
    onErrorRef.current = onError;
    const requestId = (0, react_1.useRef)(0); // 只採用最後一次請求的結果（打字很快時舊請求晚回來不覆蓋）
    // 進頁面時的條件查 initialPage；key 為 undefined＝下一次查詢不管條件都用它：
    // 同一個頁面元件換路徑（例：從已封存按上一頁回到列表第 2 頁）時，這一輪的搜尋文字可能還是舊路徑的
    const initial = (0, react_1.useRef)({ key, page: initialPage });
    const shownInitialPage = (0, react_1.useRef)(initialPage);
    if (shownInitialPage.current !== initialPage) {
        shownInitialPage.current = initialPage;
        initial.current = { key: undefined, page: initialPage };
    }
    const showCached = (0, react_1.useCallback)((k, page) => {
        const cached = resultCache.get(cacheKeyOf(topic, k, page));
        if (cached)
            setResult(cached);
    }, [topic]);
    const load = (0, react_1.useCallback)(async (page) => {
        const k = keyRef.current;
        if (!k)
            return;
        const id = ++requestId.current;
        showCached(k, page);
        setIsFetching(true);
        try {
            const response = await fetchRef.current(page);
            const next = { items: response.data.data, pagination: response.data.pagination };
            const cacheKey = cacheKeyOf(topic, k, page);
            resultCache.delete(cacheKey);
            resultCache.set(cacheKey, next);
            if (resultCache.size > CACHE_LIMIT)
                resultCache.delete(resultCache.keys().next().value);
            if (id !== requestId.current)
                return;
            setResult(next);
        }
        catch (error) {
            if (id !== requestId.current)
                return;
            console.error('Search failed:', error);
            onErrorRef.current?.(error);
        }
        finally {
            if (id === requestId.current)
                setIsFetching(false);
        }
    }, [topic, showCached]);
    const textValues = JSON.stringify(textFields.map(field => query?.[field]));
    const lastText = (0, react_1.useRef)(textValues);
    (0, react_1.useEffect)(() => {
        const searchChanged = lastText.current !== textValues;
        lastText.current = textValues;
        if (!key) {
            initial.current = null; // 這次沒有搜尋條件：列表 store 依頁面的 initialPage 載入，之後打字搜尋從第 1 頁開始
            requestId.current++;
            setResult(null);
            setIsFetching(false);
            return;
        }
        const page = initial.current && (initial.current.key === undefined || initial.current.key === key) ? initial.current.page : 1;
        if (!searchChanged) {
            initial.current = null;
            load(page);
            return;
        }
        requestId.current++; // 打字中：之前送出、還沒回來的查詢不採用
        showCached(key, page);
        const timer = setTimeout(() => {
            initial.current = null;
            load(page);
        }, 300);
        return () => clearTimeout(timer);
    }, [key, load, showCached]); // eslint-disable-line react-hooks/exhaustive-deps
    (0, dataEvents_1.useDataChangedEvent)(topic, () => {
        for (const cacheKey of [...resultCache.keys()])
            if (cacheKey.startsWith(`${topic}|`))
                resultCache.delete(cacheKey);
        if (key)
            load(result?.pagination.currentPage ?? 1);
    });
    return {
        /** 有條件、且有結果可以顯示（可能是上一個條件的，新的回來就換掉） */
        active: !!key && result !== null,
        items: result?.items ?? [],
        pagination: result?.pagination ?? null,
        /** 有條件、還沒有任何結果可以顯示（頁面這時仍顯示列表 store，通常不會用到） */
        isLoading: !!key && result === null && isFetching,
        /** 背景查詢中（換條件、換頁、更新） */
        isFetching,
        setPage: load,
    };
}
